import React, { useState, useEffect } from 'react';
import { orderAPI } from '../../services/api.js';
import toast from 'react-hot-toast';

const OrdersManagement = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [stats, setStats] = useState({
    totalOrders: 0,
    pendingOrders: 0,
    completedOrders: 0,
    cancelledOrders: 0,
    totalRevenue: 0
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrdersCount, setTotalOrdersCount] = useState(0);
  const ordersPerPage = 10;

  // Responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 480);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsSmallScreen(window.innerWidth < 480);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch orders and stats
  const fetchOrders = async () => {
    try {
      setLoading(true);
      
      const token = localStorage.getItem('adminToken');
      
      // Calculate skip for pagination
      const skip = (currentPage - 1) * ordersPerPage;
      
      // Fetch orders with pagination
      const ordersResponse = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/orders/admin?limit=${ordersPerPage}&skip=${skip}${statusFilter !== 'all' ? `&status=${statusFilter}` : ''}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      const ordersData = await ordersResponse.json();
      
      // Fetch stats
      const statsResponse = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/orders/admin/stats`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      const statsData = await statsResponse.json();
      
      console.log('Orders response:', ordersData);
      console.log('Stats response:', statsData);
      
      // Handle orders response
      if (ordersData.success) {
        const ordersList = ordersData.data || [];
        setOrders(ordersList);
        
        // Update total count if available
        if (ordersData.total !== undefined) {
          setTotalOrdersCount(ordersData.total);
          setTotalPages(Math.ceil(ordersData.total / ordersPerPage));
        } else {
          // If backend doesn't provide total, estimate from current page
          setTotalOrdersCount(ordersList.length + (currentPage - 1) * ordersPerPage);
          setTotalPages(currentPage + (ordersList.length === ordersPerPage ? 1 : 0));
        }
      } else {
        setOrders([]);
        setTotalOrdersCount(0);
        setTotalPages(1);
        toast.error(ordersData.message || 'Failed to load orders');
      }
      
      // Handle stats response
      if (statsData.success) {
        setStats(statsData.data || {
          totalOrders: 0,
          pendingOrders: 0,
          completedOrders: 0,
          cancelledOrders: 0,
          totalRevenue: 0
        });
      } else {
        setStats({
          totalOrders: 0,
          pendingOrders: 0,
          completedOrders: 0,
          cancelledOrders: 0,
          totalRevenue: 0
        });
      }
      
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load orders');
      setOrders([]);
      setTotalOrdersCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [statusFilter, currentPage]);

  // Handle page change
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = isSmallScreen ? 3 : (isMobile ? 5 : 7);
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    // Adjust if we're at the beginning
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    
    return pageNumbers;
  };

  // Update order status
  const updateOrderStatus = async (orderId, newStatus, notes = '') => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/orders/admin/${orderId}/status`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ status: newStatus, notes })
        }
      );

      const data = await response.json();

      if (data.success) {
        toast.success(data.message || `Order status updated to ${newStatus}`);
        fetchOrders(); // Refresh the list
        setSelectedOrder(null); // Close modal
      } else {
        throw new Error(data.message || 'Failed to update order status');
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      toast.error(error.message || 'Failed to update order status');
    }
  };

  // Cancel order
  const cancelOrder = async (orderId) => {
    if (window.confirm('Are you sure you want to cancel this order?')) {
      try {
        const token = localStorage.getItem('adminToken');
        const response = await fetch(
          `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/orders/${orderId}/cancel`,
          {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        const data = await response.json();

        if (data.success) {
          toast.success('Order cancelled successfully');
          fetchOrders(); // Refresh the list
        } else {
          throw new Error(data.message || 'Failed to cancel order');
        }
      } catch (error) {
        console.error('Error cancelling order:', error);
        toast.error(error.message || 'Failed to cancel order');
      }
    }
  };

  // Format date - RESPONSIVE
  const formatDate = (dateString) => {
    try {
      if (isSmallScreen) {
        return new Date(dateString).toLocaleDateString('en-US', {
          month: 'numeric',
          day: 'numeric'
        });
      }
      if (isMobile) {
        return new Date(dateString).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'var(--warning)';
      case 'completed': return 'var(--success)';
      case 'cancelled': return 'var(--error)';
      default: return 'var(--text-light)';
    }
  };

  // Get payment status color
  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'var(--success)';
      case 'pending': return 'var(--warning)';
      case 'failed': return 'var(--error)';
      default: return 'var(--text-light)';
    }
  };

  // Status display helper for mobile
  const getStatusDisplay = (status, isSmallScreen = false) => {
    const statusMap = {
      'pending': { text: 'Pending', short: 'Pend' },
      'completed': { text: 'Completed', short: 'Comp' },
      'cancelled': { text: 'Cancelled', short: 'Canc' },
      'paid': { text: 'Paid', short: 'Paid' },
      'failed': { text: 'Failed', short: 'Fail' }
    };
    
    const statusInfo = statusMap[status?.toLowerCase()] || { 
      text: status || 'Unknown', 
      short: status?.substring(0, 4) || 'Unkn' 
    };
    
    return isSmallScreen ? statusInfo.short : statusInfo.text;
  };

  return (
    <div className="admin-content" style={{ 
      padding: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-4)' : 'var(--space-6)'),
      maxWidth: '100%',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <div style={{ 
        marginBottom: isSmallScreen ? 'var(--space-3)' : (isMobile ? 'var(--space-5)' : 'var(--space-6)'),
        paddingBottom: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-3)' : 'var(--space-4)'),
        borderBottom: '1px solid var(--border-light)'
      }}>
        <h1 className="accent-gold" style={{ 
          fontSize: isSmallScreen ? 'var(--font-size-lg)' : (isMobile ? 'var(--font-size-xl)' : 'var(--font-size-3xl)'),
          marginBottom: isSmallScreen ? 'var(--space-1)' : (isMobile ? 'var(--space-2)' : 'var(--space-3)')
        }}>
          Orders <span style={{ color: 'var(--text-dark)' }}>Management</span>
        </h1>
        
        <div style={{ 
          display: 'flex', 
          flexDirection: isSmallScreen ? 'column' : (isMobile ? 'column' : 'row'),
          alignItems: isSmallScreen ? 'flex-start' : (isMobile ? 'flex-start' : 'center'),
          gap: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-3)' : 'var(--space-4)'),
          justifyContent: 'space-between'
        }}>
          <p style={{ 
            color: 'var(--text-medium)',
            fontSize: isSmallScreen ? 'var(--font-size-xs)' : (isMobile ? 'var(--font-size-sm)' : 'var(--font-size-base)')
          }}>
            Manage customer orders and track order status
          </p>
          
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: isSmallScreen ? 'var(--space-2)' : 'var(--space-3)'
          }}>
            <button 
              onClick={fetchOrders}
              className="btn btn-secondary"
              disabled={loading}
              style={{ 
                padding: isSmallScreen ? '6px 10px' : (isMobile ? 'var(--space-2)' : 'var(--space-3) var(--space-4)'),
                fontSize: isSmallScreen ? 'var(--font-size-xs)' : (isMobile ? 'var(--font-size-sm)' : 'var(--font-size-base)')
              }}
            >
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      {/* Order Stats */}
      <div style={{ 
        marginBottom: isSmallScreen ? 'var(--space-3)' : (isMobile ? 'var(--space-5)' : 'var(--space-6)') 
      }}>
        <h2 style={{ 
          fontSize: isSmallScreen ? 'var(--font-size-base)' : (isMobile ? 'var(--font-size-lg)' : 'var(--font-size-xl)'),
          marginBottom: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-3)' : 'var(--space-4)'),
          color: 'var(--text-dark)'
        }}>
          Order Statistics
        </h2>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: isSmallScreen ? 'repeat(2, 1fr)' : 
                               isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: isSmallScreen ? '8px' : (isMobile ? 'var(--space-3)' : 'var(--space-4)')
        }}>
          {/* Total Orders */}
          <div className="stat-card gold" style={{
            padding: isSmallScreen ? '10px' : (isMobile ? 'var(--space-3)' : 'var(--space-4)'),
            minHeight: isSmallScreen ? '80px' : (isMobile ? '100px' : '120px'),
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}>
            <div className="stat-value" style={{ 
              color: 'var(--accent-gold)',
              fontSize: isSmallScreen ? 'var(--font-size-lg)' : (isMobile ? 'var(--font-size-xl)' : 'var(--font-size-2xl)'),
              lineHeight: 1.2,
              marginBottom: '4px'
            }}>
              {stats.totalOrders}
            </div>
            <div style={{ 
              color: 'var(--text-light)', 
              fontSize: isSmallScreen ? 'var(--font-size-xs)' : (isMobile ? 'var(--font-size-sm)' : 'var(--font-size-base)'),
              fontWeight: '500'
            }}>
              Total Orders
            </div>
          </div>
          
          {/* Pending Orders */}
          <div className="stat-card" style={{
            padding: isSmallScreen ? '10px' : (isMobile ? 'var(--space-3)' : 'var(--space-4)'),
            minHeight: isSmallScreen ? '80px' : (isMobile ? '100px' : '120px'),
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}>
            <div className="stat-value" style={{ 
              color: 'var(--warning)',
              fontSize: isSmallScreen ? 'var(--font-size-lg)' : (isMobile ? 'var(--font-size-xl)' : 'var(--font-size-2xl)'),
              lineHeight: 1.2,
              marginBottom: '4px'
            }}>
              {stats.pendingOrders}
            </div>
            <div style={{ 
              color: 'var(--text-light)', 
              fontSize: isSmallScreen ? 'var(--font-size-xs)' : (isMobile ? 'var(--font-size-sm)' : 'var(--font-size-base)'),
              fontWeight: '500'
            }}>
              Pending
            </div>
          </div>
          
          {/* Completed Orders */}
          <div className="stat-card" style={{
            padding: isSmallScreen ? '10px' : (isMobile ? 'var(--space-3)' : 'var(--space-4)'),
            minHeight: isSmallScreen ? '80px' : (isMobile ? '100px' : '120px'),
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}>
            <div className="stat-value" style={{ 
              color: 'var(--success)',
              fontSize: isSmallScreen ? 'var(--font-size-lg)' : (isMobile ? 'var(--font-size-xl)' : 'var(--font-size-2xl)'),
              lineHeight: 1.2,
              marginBottom: '4px'
            }}>
              {stats.completedOrders}
            </div>
            <div style={{ 
              color: 'var(--text-light)', 
              fontSize: isSmallScreen ? 'var(--font-size-xs)' : (isMobile ? 'var(--font-size-sm)' : 'var(--font-size-base)'),
              fontWeight: '500'
            }}>
              Completed
            </div>
          </div>
          
          {/* Cancelled Orders */}
          <div className="stat-card" style={{
            padding: isSmallScreen ? '10px' : (isMobile ? 'var(--space-3)' : 'var(--space-4)'),
            minHeight: isSmallScreen ? '80px' : (isMobile ? '100px' : '120px'),
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}>
            <div className="stat-value" style={{ 
              color: 'var(--error)',
              fontSize: isSmallScreen ? 'var(--font-size-lg)' : (isMobile ? 'var(--font-size-xl)' : 'var(--font-size-2xl)'),
              lineHeight: 1.2,
              marginBottom: '4px'
            }}>
              {stats.cancelledOrders || 0}
            </div>
            <div style={{ 
              color: 'var(--text-light)', 
              fontSize: isSmallScreen ? 'var(--font-size-xs)' : (isMobile ? 'var(--font-size-sm)' : 'var(--font-size-base)'),
              fontWeight: '500'
            }}>
              Cancelled
            </div>
          </div>
          
          {/* Total Revenue */}
          <div className="stat-card" style={{
            padding: isSmallScreen ? '10px' : (isMobile ? 'var(--space-3)' : 'var(--space-4)'),
            minHeight: isSmallScreen ? '80px' : (isMobile ? '100px' : '120px'),
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}>
            <div className="stat-value" style={{ 
              color: 'var(--info)',
              fontSize: isSmallScreen ? 'var(--font-size-lg)' : (isMobile ? 'var(--font-size-xl)' : 'var(--font-size-2xl)'),
              lineHeight: 1.2,
              marginBottom: '4px',
              wordBreak: 'break-word'
            }}>
              KSh {stats.totalRevenue?.toLocaleString() || '0'}
            </div>
            <div style={{ 
              color: 'var(--text-light)', 
              fontSize: isSmallScreen ? 'var(--font-size-xs)' : (isMobile ? 'var(--font-size-sm)' : 'var(--font-size-base)'),
              fontWeight: '500'
            }}>
              Total Revenue
            </div>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="card card-gold" style={{ marginBottom: isSmallScreen ? 'var(--space-3)' : (isMobile ? 'var(--space-5)' : 'var(--space-6)') }}>
        <div className="card-header" style={{
          padding: isSmallScreen ? 'var(--space-2) var(--space-3)' : (isMobile ? 'var(--space-3) var(--space-4)' : 'var(--space-4) var(--space-5)'),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: isSmallScreen ? 'wrap' : (isMobile ? 'wrap' : 'nowrap'),
          gap: isSmallScreen ? 'var(--space-1)' : (isMobile ? 'var(--space-2)' : '0')
        }}>
          <h3 style={{ 
            margin: 0,
            fontSize: isSmallScreen ? 'var(--font-size-sm)' : (isMobile ? 'var(--font-size-base)' : 'var(--font-size-lg)'),
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)'
          }}>
            📦 All Orders ({totalOrdersCount})
          </h3>
          
          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1); // Reset to first page when filter changes
            }}
            className="form-select"
            style={{ 
              width: isSmallScreen ? '100%' : 'auto',
              marginTop: isSmallScreen ? 'var(--space-1)' : '0',
              padding: isSmallScreen ? '6px 10px' : (isMobile ? 'var(--space-2)' : 'var(--space-3)'),
              fontSize: isSmallScreen ? 'var(--font-size-xs)' : (isMobile ? 'var(--font-size-sm)' : 'var(--font-size-base)')
            }}
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        
        <div className="card-body" style={{
          padding: isSmallScreen ? 'var(--space-3)' : (isMobile ? 'var(--space-4)' : 'var(--space-5)')
        }}>
          {loading ? (
            <div style={{ 
              textAlign: 'center', 
              padding: isSmallScreen ? 'var(--space-4)' : 'var(--space-8)', 
              color: 'var(--text-light)'
            }}>
              Loading orders...
            </div>
          ) : orders.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: isSmallScreen ? 'var(--space-4)' : 'var(--space-8)', 
              color: 'var(--text-light)'
            }}>
              <p style={{ fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-base)' }}>
                No orders found.
              </p>
              <p style={{ 
                fontSize: isSmallScreen ? '9px' : 'var(--font-size-sm)',
                marginTop: 'var(--space-1)'
              }}>
                {statusFilter !== 'all' ? `No orders with status "${statusFilter}"` : 'No orders have been placed yet.'}
              </p>
            </div>
          ) : isSmallScreen ? (
            // Extra small screen view (card layout)
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {orders.map((order) => (
                <div key={order._id} style={{
                  padding: '12px',
                  backgroundColor: 'var(--bg-light)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-light)',
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr',
                  gap: '12px',
                  alignItems: 'start'
                }}>
                  {/* Status & Date */}
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <div style={{ 
                      backgroundColor: getStatusColor(order.status),
                      color: 'white',
                      padding: '4px 6px',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: '9px',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      textAlign: 'center',
                      minWidth: '40px'
                    }}>
                      {getStatusDisplay(order.status, true)}
                    </div>
                    <div style={{ 
                      fontSize: '8px', 
                      color: 'var(--text-light)',
                      textAlign: 'center'
                    }}>
                      {order.createdAt ? formatDate(order.createdAt) : ''}
                    </div>
                  </div>
                  
                  {/* Order Details */}
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: '4px'
                  }}>
                    <div style={{ 
                      fontWeight: '700', 
                      color: 'var(--text-dark)',
                      fontSize: 'var(--font-size-xs)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      #{order.orderNumber || 'N/A'}
                    </div>
                    
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      fontSize: '9px'
                    }}>
                      <span style={{ color: 'var(--text-medium)' }}>Customer:</span>
                      <span style={{ fontWeight: '500' }}>
                        {order.recipient?.firstName?.charAt(0) || 'C'}. {order.recipient?.lastName?.charAt(0) || ''}
                      </span>
                    </div>
                    
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      fontSize: '9px'
                    }}>
                      <span style={{ color: 'var(--text-medium)' }}>Items:</span>
                      <span style={{ fontWeight: '500' }}>
                        {order.items?.length || 0}
                      </span>
                    </div>
                    
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      fontSize: '9px'
                    }}>
                      <span style={{ color: 'var(--text-medium)' }}>Amount:</span>
                      <span style={{ fontWeight: '700', color: 'var(--accent-gold)' }}>
                        KSh {order.totalAmount?.toLocaleString() || '0'}
                      </span>
                    </div>
                    
                    {/* Payment Status */}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      fontSize: '8px',
                      marginTop: '2px'
                    }}>
                      <span style={{ color: 'var(--text-medium)' }}>Payment:</span>
                      <span style={{ 
                        fontWeight: '600',
                        color: getPaymentStatusColor(order.paymentStatus) === 'var(--success)' ? 'var(--success)' : 'var(--warning)'
                      }}>
                        {getStatusDisplay(order.paymentStatus, true)}
                      </span>
                    </div>
                    
                    {/* Actions */}
                    <div style={{ 
                      display: 'flex', 
                      gap: '4px',
                      marginTop: '4px'
                    }}>
                      <button 
                        className="btn btn-secondary"
                        style={{ 
                          padding: '4px 8px',
                          fontSize: '9px',
                          flex: 1
                        }}
                        onClick={() => setSelectedOrder(order)}
                      >
                        View
                      </button>
                      
                      {order.status === 'pending' && (
                        <button 
                          className="btn btn-danger"
                          style={{ 
                            padding: '4px 8px',
                            fontSize: '9px',
                            flex: 1
                          }}
                          onClick={() => cancelOrder(order._id)}
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : isMobile ? (
            // Mobile view: Card layout
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              {orders.map((order) => (
                <div key={order._id} style={{
                  padding: 'var(--space-3)',
                  backgroundColor: 'var(--bg-light)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-light)'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 'var(--space-2)'
                  }}>
                    <div style={{ 
                      fontWeight: '700', 
                      color: 'var(--text-dark)',
                      fontSize: 'var(--font-size-sm)'
                    }}>
                      #{order.orderNumber || 'N/A'}
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px'
                    }}>
                      <span style={{ 
                        fontSize: 'var(--font-size-xs)', 
                        color: 'var(--text-light)'
                      }}>
                        {order.createdAt ? formatDate(order.createdAt) : ''}
                      </span>
                      <span style={{ 
                        backgroundColor: getStatusColor(order.status),
                        color: 'white',
                        padding: '4px 10px',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: '600',
                        textTransform: 'capitalize'
                      }}>
                        {getStatusDisplay(order.status, false)}
                      </span>
                    </div>
                  </div>
                  
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: '6px',
                    fontSize: 'var(--font-size-sm)'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--text-medium)' }}>Customer:</span>
                      <span style={{ fontWeight: '500' }}>
                        {order.recipient?.firstName || 'Customer'} {order.recipient?.lastName || ''}
                      </span>
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--text-medium)' }}>Items:</span>
                      <span style={{ fontWeight: '500' }}>
                        {order.items?.length || 0} items
                      </span>
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--text-medium)' }}>Amount:</span>
                      <span style={{ fontWeight: '700', color: 'var(--accent-gold)' }}>
                        KSh {order.totalAmount?.toLocaleString() || '0'}
                      </span>
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--text-medium)' }}>Payment:</span>
                      <span style={{ 
                        fontWeight: '600',
                        color: getPaymentStatusColor(order.paymentStatus) === 'var(--success)' ? 'var(--success)' : 'var(--warning)'
                      }}>
                        {getStatusDisplay(order.paymentStatus, false)}
                      </span>
                    </div>
                    
                    {/* Actions */}
                    <div style={{ 
                      display: 'flex', 
                      gap: 'var(--space-2)',
                      marginTop: 'var(--space-2)'
                    }}>
                      <button 
                        className="btn btn-secondary"
                        style={{ 
                          padding: 'var(--space-2) var(--space-3)',
                          fontSize: 'var(--font-size-xs)',
                          flex: 1
                        }}
                        onClick={() => setSelectedOrder(order)}
                      >
                        View Details
                      </button>
                      
                      {order.status === 'pending' && (
                        <button 
                          className="btn btn-danger"
                          style={{ 
                            padding: 'var(--space-2) var(--space-3)',
                            fontSize: 'var(--font-size-xs)',
                            flex: 1
                          }}
                          onClick={() => cancelOrder(order._id)}
                        >
                          Cancel Order
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Desktop view: Table layout with horizontal scroll
            <div style={{ 
              overflowX: 'auto',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-light)'
            }}>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse',
                minWidth: '800px'
              }}>
                <thead>
                  <tr style={{ 
                    borderBottom: '2px solid var(--border-light)',
                    backgroundColor: 'var(--bg-light)'
                  }}>
                    <th style={{ 
                      padding: isSmallScreen ? '8px 12px' : (isMobile ? 'var(--space-2) var(--space-3)' : 'var(--space-3) var(--space-4)'), 
                      textAlign: 'left', 
                      color: 'var(--text-medium)',
                      fontWeight: '600',
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                      whiteSpace: 'nowrap'
                    }}>Order #</th>
                    <th style={{ 
                      padding: isSmallScreen ? '8px 12px' : (isMobile ? 'var(--space-2) var(--space-3)' : 'var(--space-3) var(--space-4)'), 
                      textAlign: 'left', 
                      color: 'var(--text-medium)',
                      fontWeight: '600',
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                      whiteSpace: 'nowrap'
                    }}>Customer</th>
                    <th style={{ 
                      padding: isSmallScreen ? '8px 12px' : (isMobile ? 'var(--space-2) var(--space-3)' : 'var(--space-3) var(--space-4)'), 
                      textAlign: 'left', 
                      color: 'var(--text-medium)',
                      fontWeight: '600',
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                      whiteSpace: 'nowrap'
                    }}>Items</th>
                    <th style={{ 
                      padding: isSmallScreen ? '8px 12px' : (isMobile ? 'var(--space-2) var(--space-3)' : 'var(--space-3) var(--space-4)'), 
                      textAlign: 'left', 
                      color: 'var(--text-medium)',
                      fontWeight: '600',
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                      whiteSpace: 'nowrap'
                    }}>Total</th>
                    <th style={{ 
                      padding: isSmallScreen ? '8px 12px' : (isMobile ? 'var(--space-2) var(--space-3)' : 'var(--space-3) var(--space-4)'), 
                      textAlign: 'left', 
                      color: 'var(--text-medium)',
                      fontWeight: '600',
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                      whiteSpace: 'nowrap'
                    }}>Status</th>
                    <th style={{ 
                      padding: isSmallScreen ? '8px 12px' : (isMobile ? 'var(--space-2) var(--space-3)' : 'var(--space-3) var(--space-4)'), 
                      textAlign: 'left', 
                      color: 'var(--text-medium)',
                      fontWeight: '600',
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                      whiteSpace: 'nowrap'
                    }}>Payment</th>
                    <th style={{ 
                      padding: isSmallScreen ? '8px 12px' : (isMobile ? 'var(--space-2) var(--space-3)' : 'var(--space-3) var(--space-4)'), 
                      textAlign: 'left', 
                      color: 'var(--text-medium)',
                      fontWeight: '600',
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                      whiteSpace: 'nowrap'
                    }}>Date</th>
                    <th style={{ 
                      padding: isSmallScreen ? '8px 12px' : (isMobile ? 'var(--space-2) var(--space-3)' : 'var(--space-3) var(--space-4)'), 
                      textAlign: 'left', 
                      color: 'var(--text-medium)',
                      fontWeight: '600',
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                      whiteSpace: 'nowrap'
                    }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr key={order._id} style={{ 
                      borderBottom: '1px solid var(--border-light)',
                      transition: 'background-color 0.2s ease'
                    }}>
                      <td style={{ 
                        padding: isSmallScreen ? '8px 12px' : (isMobile ? 'var(--space-2) var(--space-3)' : 'var(--space-3) var(--space-4)'),
                        fontWeight: '600',
                        fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                      }}>
                        <div style={{ color: 'var(--text-dark)' }}>
                          {order.orderNumber}
                        </div>
                        <div style={{ 
                          fontSize: isSmallScreen ? '9px' : 'var(--font-size-xs)', 
                          color: 'var(--text-light)'
                        }}>
                          {order._id?.substring(-8) || 'N/A'}
                        </div>
                      </td>
                      
                      <td style={{ 
                        padding: isSmallScreen ? '8px 12px' : (isMobile ? 'var(--space-2) var(--space-3)' : 'var(--space-3) var(--space-4)')
                      }}>
                        <div style={{ 
                          fontWeight: '500', 
                          color: 'var(--text-dark)',
                          fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                        }}>
                          {order.recipient?.firstName} {order.recipient?.lastName}
                        </div>
                        <div style={{ 
                          fontSize: isSmallScreen ? '9px' : 'var(--font-size-xs)', 
                          color: 'var(--text-light)'
                        }}>
                          {order.recipient?.email}
                        </div>
                        {order.recipient?.phone && (
                          <div style={{ 
                            fontSize: isSmallScreen ? '9px' : 'var(--font-size-xs)', 
                            color: 'var(--text-light)'
                          }}>
                            📞 {order.recipient.phone}
                          </div>
                        )}
                      </td>
                      
                      <td style={{ 
                        padding: isSmallScreen ? '8px 12px' : (isMobile ? 'var(--space-2) var(--space-3)' : 'var(--space-3) var(--space-4)')
                      }}>
                        <div style={{ 
                          fontWeight: '500', 
                          color: 'var(--text-dark)',
                          fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                        }}>
                          {order.items?.length || 0} items
                        </div>
                        <div style={{ 
                          fontSize: isSmallScreen ? '9px' : 'var(--font-size-xs)', 
                          color: 'var(--text-light)'
                        }}>
                          {order.items?.[0]?.name || 'N/A'}
                          {order.items?.length > 1 && ` +${order.items.length - 1} more`}
                        </div>
                      </td>
                      
                      <td style={{ 
                        padding: isSmallScreen ? '8px 12px' : (isMobile ? 'var(--space-2) var(--space-3)' : 'var(--space-3) var(--space-4)'),
                        fontWeight: '600', 
                        color: 'var(--accent-gold)',
                        fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                      }}>
                        KSh {order.totalAmount?.toLocaleString() || '0'}
                      </td>
                      
                      <td style={{ 
                        padding: isSmallScreen ? '8px 12px' : (isMobile ? 'var(--space-2) var(--space-3)' : 'var(--space-3) var(--space-4)')
                      }}>
                        <span 
                          style={{ 
                            backgroundColor: getStatusColor(order.status),
                            color: 'white',
                            padding: '4px 10px',
                            borderRadius: 'var(--radius-sm)',
                            fontSize: isSmallScreen ? '10px' : 'var(--font-size-xs)',
                            fontWeight: '600',
                            textTransform: 'capitalize',
                            display: 'inline-block',
                            minWidth: isSmallScreen ? '60px' : '80px',
                            textAlign: 'center'
                          }}
                        >
                          {getStatusDisplay(order.status, isSmallScreen)}
                        </span>
                      </td>
                      
                      <td style={{ 
                        padding: isSmallScreen ? '8px 12px' : (isMobile ? 'var(--space-2) var(--space-3)' : 'var(--space-3) var(--space-4)')
                      }}>
                        <span 
                          style={{ 
                            backgroundColor: getPaymentStatusColor(order.paymentStatus),
                            color: 'white',
                            padding: '4px 10px',
                            borderRadius: 'var(--radius-sm)',
                            fontSize: isSmallScreen ? '10px' : 'var(--font-size-xs)',
                            fontWeight: '600',
                            textTransform: 'capitalize',
                            display: 'inline-block',
                            minWidth: isSmallScreen ? '40px' : '60px',
                            textAlign: 'center'
                          }}
                        >
                          {getStatusDisplay(order.paymentStatus, isSmallScreen)}
                        </span>
                      </td>
                      
                      <td style={{ 
                        padding: isSmallScreen ? '8px 12px' : (isMobile ? 'var(--space-2) var(--space-3)' : 'var(--space-3) var(--space-4)'),
                        fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                        color: 'var(--text-dark)'
                      }}>
                        {order.createdAt ? formatDate(order.createdAt) : 'Unknown date'}
                      </td>
                      
                      <td style={{ 
                        padding: isSmallScreen ? '8px 12px' : (isMobile ? 'var(--space-2) var(--space-3)' : 'var(--space-3) var(--space-4)')
                      }}>
                        <div style={{ 
                          display: 'flex', 
                          gap: isSmallScreen ? '4px' : 'var(--space-2)',
                          flexWrap: 'wrap'
                        }}>
                          <button 
                            className="btn btn-secondary"
                            style={{ 
                              padding: isSmallScreen ? '4px 8px' : 'var(--space-2) var(--space-3)',
                              fontSize: isSmallScreen ? '10px' : 'var(--font-size-xs)'
                            }}
                            onClick={() => setSelectedOrder(order)}
                          >
                            View
                          </button>
                          
                          {order.status === 'pending' && (
                            <button 
                              className="btn btn-danger"
                              style={{ 
                                padding: isSmallScreen ? '4px 8px' : 'var(--space-2) var(--space-3)',
                                fontSize: isSmallScreen ? '10px' : 'var(--font-size-xs)'
                              }}
                              onClick={() => cancelOrder(order._id)}
                            >
                              Cancel
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination Component */}
          {totalPages > 1 && (
            <div style={{
              marginTop: isSmallScreen ? 'var(--space-3)' : (isMobile ? 'var(--space-4)' : 'var(--space-5)'),
              paddingTop: isSmallScreen ? 'var(--space-2)' : 'var(--space-3)',
              borderTop: '1px solid var(--border-light)',
              display: 'flex',
              flexDirection: isSmallScreen ? 'column' : 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: isSmallScreen ? 'var(--space-2)' : '0'
            }}>
              {/* Page Info */}
              <div style={{
                fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                color: 'var(--text-medium)',
                marginBottom: isSmallScreen ? 'var(--space-1)' : '0'
              }}>
                Showing {(currentPage - 1) * ordersPerPage + 1} to {Math.min(currentPage * ordersPerPage, totalOrdersCount)} of {totalOrdersCount} orders
              </div>

              {/* Pagination Controls */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: isSmallScreen ? '4px' : 'var(--space-2)'
              }}>
                {/* First Page Button */}
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  className="btn btn-secondary"
                  style={{
                    padding: isSmallScreen ? '6px 8px' : '8px 12px',
                    fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                    opacity: currentPage === 1 ? 0.5 : 1,
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isSmallScreen ? '««' : 'First'}
                </button>

                {/* Previous Page Button */}
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="btn btn-secondary"
                  style={{
                    padding: isSmallScreen ? '6px 8px' : '8px 12px',
                    fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                    opacity: currentPage === 1 ? 0.5 : 1,
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isSmallScreen ? '«' : 'Previous'}
                </button>

                {/* Page Numbers */}
                <div style={{
                  display: 'flex',
                  gap: isSmallScreen ? '2px' : '4px'
                }}>
                  {getPageNumbers().map((pageNum) => (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={pageNum === currentPage ? 'btn btn-gold' : 'btn btn-secondary'}
                      style={{
                        padding: isSmallScreen ? '6px 8px' : '8px 12px',
                        fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                        minWidth: isSmallScreen ? '32px' : '40px',
                        fontWeight: pageNum === currentPage ? '600' : '400'
                      }}
                    >
                      {pageNum}
                    </button>
                  ))}
                </div>

                {/* Next Page Button */}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="btn btn-secondary"
                  style={{
                    padding: isSmallScreen ? '6px 8px' : '8px 12px',
                    fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                    opacity: currentPage === totalPages ? 0.5 : 1,
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isSmallScreen ? '»' : 'Next'}
                </button>

                {/* Last Page Button */}
                <button
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  className="btn btn-secondary"
                  style={{
                    padding: isSmallScreen ? '6px 8px' : '8px 12px',
                    fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                    opacity: currentPage === totalPages ? 0.5 : 1,
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isSmallScreen ? '»»' : 'Last'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Order Details Modal - RESPONSIVE */}
      {selectedOrder && (
        <OrderDetailsModal 
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          onStatusUpdate={updateOrderStatus}
          isMobile={isMobile}
          isSmallScreen={isSmallScreen}
          formatDate={formatDate}
        />
      )}
    </div>
  );
};

// Responsive Order Details Modal Component
const OrderDetailsModal = ({ order, onClose, onStatusUpdate, isMobile, isSmallScreen, formatDate }) => {
  const [status, setStatus] = useState(order.status);
  const [notes, setNotes] = useState(order.notes || '');
  const [updating, setUpdating] = useState(false);

  const handleStatusUpdate = async () => {
    if (status !== order.status || notes !== order.notes) {
      setUpdating(true);
      await onStatusUpdate(order._id, status, notes);
      setUpdating(false);
    } else {
      onClose();
    }
  };

  // Responsive modal styles
  const modalStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-3)' : 'var(--space-4)')
  };

  const contentStyle = {
    width: '100%',
    maxWidth: isSmallScreen ? '95vw' : (isMobile ? '90vw' : '800px'),
    maxHeight: isSmallScreen ? '85vh' : '90vh',
    overflowY: 'auto',
    borderRadius: isSmallScreen ? 'var(--radius-md)' : 'var(--radius-lg)'
  };

  return (
    <div style={modalStyle}>
      <div className="card" style={contentStyle}>
        <div className="card-header" style={{
          padding: isSmallScreen ? 'var(--space-2) var(--space-3)' : (isMobile ? 'var(--space-3) var(--space-4)' : 'var(--space-4) var(--space-5)'),
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h3 style={{ 
            margin: 0,
            fontSize: isSmallScreen ? 'var(--font-size-sm)' : (isMobile ? 'var(--font-size-base)' : 'var(--font-size-lg)'),
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)'
          }}>
            <span style={{ color: 'var(--accent-gold)' }}>📦</span>
            Order: {order.orderNumber}
          </h3>
          <button 
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: isSmallScreen ? '1.3rem' : '1.5rem',
              cursor: 'pointer',
              color: 'var(--text-light)',
              padding: isSmallScreen ? '4px' : '8px',
              borderRadius: 'var(--radius-sm)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>

        <div className="card-body" style={{
          padding: isSmallScreen ? 'var(--space-3)' : (isMobile ? 'var(--space-4)' : 'var(--space-5)')
        }}>
          <div style={{ display: 'grid', gap: isSmallScreen ? 'var(--space-3)' : (isMobile ? 'var(--space-4)' : 'var(--space-6)') }}>
            
            {/* Order Summary */}
            <div>
              <h4 style={{ 
                marginBottom: isSmallScreen ? 'var(--space-2)' : 'var(--space-4)',
                color: 'var(--text-dark)',
                borderBottom: '2px solid var(--accent-gold)',
                paddingBottom: isSmallScreen ? 'var(--space-1)' : 'var(--space-2)',
                fontSize: isSmallScreen ? 'var(--font-size-sm)' : (isMobile ? 'var(--font-size-base)' : 'var(--font-size-lg)')
              }}>
                Order Summary
              </h4>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: isSmallScreen ? '1fr' : (isMobile ? '1fr 1fr' : '1fr 1fr'),
                gap: isSmallScreen ? '8px' : (isMobile ? 'var(--space-3)' : 'var(--space-4)'),
                fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
              }}>
                <div>
                  <strong>Order Number:</strong> {order.orderNumber}
                </div>
                <div>
                  <strong>Order Date:</strong> {formatDate(order.createdAt)}
                </div>
                <div>
                  <strong>Total Amount:</strong> KSh {order.totalAmount?.toLocaleString()}
                </div>
                <div>
                  <strong>Payment Status:</strong> 
                  <span style={{ 
                    marginLeft: 'var(--space-2)',
                    padding: '2px 8px',
                    borderRadius: 'var(--radius-sm)',
                    backgroundColor: order.paymentStatus === 'paid' ? 'var(--success)' : 'var(--warning)',
                    color: 'white',
                    fontSize: isSmallScreen ? '9px' : 'var(--font-size-xs)',
                    fontWeight: '600',
                    display: 'inline-block'
                  }}>
                    {order.paymentStatus}
                  </span>
                </div>
              </div>
            </div>

            {/* Customer Information */}
            <div>
              <h4 style={{ 
                marginBottom: isSmallScreen ? 'var(--space-2)' : 'var(--space-4)',
                color: 'var(--text-dark)',
                borderBottom: '2px solid var(--accent-gold)',
                paddingBottom: isSmallScreen ? 'var(--space-1)' : 'var(--space-2)',
                fontSize: isSmallScreen ? 'var(--font-size-sm)' : (isMobile ? 'var(--font-size-base)' : 'var(--font-size-lg)')
              }}>
                Customer Information
              </h4>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: isSmallScreen ? '1fr' : (isMobile ? '1fr 1fr' : '1fr 1fr'),
                gap: isSmallScreen ? '8px' : (isMobile ? 'var(--space-3)' : 'var(--space-4)'),
                fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
              }}>
                <div>
                  <strong>Name:</strong> {order.recipient?.firstName} {order.recipient?.lastName}
                </div>
                <div>
                  <strong>Email:</strong> {order.recipient?.email}
                </div>
                <div>
                  <strong>Phone:</strong> {order.recipient?.phone}
                </div>
                <div>
                  <strong>ID Number:</strong> {order.recipient?.idNumber || 'N/A'}
                </div>
              </div>
            </div>

            {/* Pickup Information */}
            {order.pickup && (
              <div>
                <h4 style={{ 
                  marginBottom: isSmallScreen ? 'var(--space-2)' : 'var(--space-4)',
                  color: 'var(--text-dark)',
                  borderBottom: '2px solid var(--accent-gold)',
                  paddingBottom: isSmallScreen ? 'var(--space-1)' : 'var(--space-2)',
                  fontSize: isSmallScreen ? 'var(--font-size-sm)' : (isMobile ? 'var(--font-size-base)' : 'var(--font-size-lg)')
                }}>
                  Pickup Information
                </h4>
                <div style={{ 
                  display: 'grid', 
                  gap: isSmallScreen ? '6px' : 'var(--space-3)',
                  fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                }}>
                  <div><strong>Pickup Option:</strong> {order.pickup?.option || 'N/A'}</div>
                  <div><strong>Station:</strong> {order.pickup?.station || 'N/A'}</div>
                  <div><strong>County:</strong> {order.pickup?.county || 'N/A'}</div>
                  {order.pickup?.stationDetails?.address && (
                    <div><strong>Address:</strong> {order.pickup.stationDetails.address}</div>
                  )}
                  {order.pickup?.stationDetails?.contact && (
                    <div><strong>Contact:</strong> {order.pickup.stationDetails.contact}</div>
                  )}
                </div>
              </div>
            )}

            {/* Order Items */}
            <div>
              <h4 style={{ 
                marginBottom: isSmallScreen ? 'var(--space-2)' : 'var(--space-4)',
                color: 'var(--text-dark)',
                borderBottom: '2px solid var(--accent-gold)',
                paddingBottom: isSmallScreen ? 'var(--space-1)' : 'var(--space-2)',
                fontSize: isSmallScreen ? 'var(--font-size-sm)' : (isMobile ? 'var(--font-size-base)' : 'var(--font-size-lg)')
              }}>
                Order Items ({order.items?.length || 0})
              </h4>
              <div style={{ 
                border: '1px solid var(--border-light)', 
                borderRadius: 'var(--radius-md)',
                maxHeight: isSmallScreen ? '200px' : (isMobile ? '300px' : '400px'),
                overflowY: 'auto'
              }}>
                {order.items?.map((item, index) => (
                  <div key={index} style={{
                    padding: isSmallScreen ? '10px' : (isMobile ? 'var(--space-2) var(--space-3)' : 'var(--space-3)'),
                    borderBottom: index < order.items.length - 1 ? '1px solid var(--border-light)' : 'none',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: isSmallScreen ? '8px' : 'var(--space-3)',
                      flex: 1
                    }}>
                      {item.image && !isSmallScreen && (
                        <img 
                          src={item.image} 
                          alt={item.name}
                          style={{
                            width: isSmallScreen ? '30px' : (isMobile ? '40px' : '50px'),
                            height: isSmallScreen ? '30px' : (isMobile ? '40px' : '50px'),
                            borderRadius: 'var(--radius-md)',
                            objectFit: 'cover'
                          }}
                        />
                      )}
                      <div style={{ flex: 1 }}>
                        <div style={{ 
                          fontWeight: '600', 
                          color: 'var(--text-dark)',
                          fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                          marginBottom: '2px'
                        }}>
                          {item.name}
                        </div>
                        <div style={{ 
                          fontSize: isSmallScreen ? '9px' : 'var(--font-size-xs)', 
                          color: 'var(--text-light)'
                        }}>
                          Quantity: {item.quantity}
                          {item.isBooking && (
                            <span style={{ 
                              marginLeft: 'var(--space-2)', 
                              color: 'var(--accent-gold)',
                              fontWeight: '600'
                            }}>
                              • Pre-order
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div style={{ 
                      fontWeight: '600', 
                      color: 'var(--text-dark)',
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                      minWidth: '70px',
                      textAlign: 'right'
                    }}>
                      KSh {(item.price * item.quantity).toLocaleString()}
                    </div>
                  </div>
                ))}
                <div style={{
                  padding: isSmallScreen ? '12px' : (isMobile ? 'var(--space-3)' : 'var(--space-4)'),
                  backgroundColor: 'var(--bg-light)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontWeight: '600',
                  fontSize: isSmallScreen ? 'var(--font-size-sm)' : 'var(--font-size-base)'
                }}>
                  <div>Total Amount:</div>
                  <div>KSh {order.totalAmount?.toLocaleString()}</div>
                </div>
              </div>
            </div>

            {/* Order Status Update */}
            <div>
              <h4 style={{ 
                marginBottom: isSmallScreen ? 'var(--space-2)' : 'var(--space-4)',
                color: 'var(--text-dark)',
                borderBottom: '2px solid var(--accent-gold)',
                paddingBottom: isSmallScreen ? 'var(--space-1)' : 'var(--space-2)',
                fontSize: isSmallScreen ? 'var(--font-size-sm)' : (isMobile ? 'var(--font-size-base)' : 'var(--font-size-lg)')
              }}>
                Update Order Status
              </h4>
              <div style={{ display: 'grid', gap: isSmallScreen ? '12px' : 'var(--space-4)' }}>
                <div className="form-group">
                  <label className="form-label" style={{
                    fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                    marginBottom: '4px',
                    display: 'block'
                  }}>
                    Status
                  </label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="form-select"
                    style={{
                      width: '100%',
                      padding: isSmallScreen ? '8px 12px' : '10px 16px',
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                    }}
                  >
                    <option value="pending">Pending</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label className="form-label" style={{
                    fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                    marginBottom: '4px',
                    display: 'block'
                  }}>
                    Admin Notes
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="form-textarea"
                    placeholder="Add any notes or instructions..."
                    rows={isSmallScreen ? 2 : 3}
                    style={{
                      width: '100%',
                      padding: isSmallScreen ? '8px 12px' : '10px 16px',
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                    }}
                  />
                </div>
              </div>
            </div>

          </div>
        </div>

        <div className="card-footer" style={{ 
          padding: isSmallScreen ? 'var(--space-2) var(--space-3)' : (isMobile ? 'var(--space-3) var(--space-4)' : 'var(--space-4) var(--space-5)'),
          display: 'flex', 
          justifyContent: 'flex-end',
          gap: isSmallScreen ? '8px' : 'var(--space-3)',
          borderTop: '1px solid var(--border-light)'
        }}>
          <button 
            type="button" 
            className="btn btn-secondary" 
            onClick={onClose}
            disabled={updating}
            style={{
              padding: isSmallScreen ? '8px 12px' : '10px 20px',
              fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
            }}
          >
            Cancel
          </button>
          <button 
            type="button" 
            className="btn btn-gold" 
            onClick={handleStatusUpdate}
            disabled={updating}
            style={{
              padding: isSmallScreen ? '8px 12px' : '10px 20px',
              fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
            }}
          >
            {updating ? 'Updating...' : 'Update Status'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrdersManagement;